import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatINR, productImg } from "@/lib/api";
import { Search } from "lucide-react";

const DEFAULT_CATS = ["Glitter", "Y2K", "French", "Pastel", "Foils", "3D", "Bold"];

const Star = ({ filled = true }) => <span style={{ color: filled ? "var(--bloom)" : "#F0C9D6" }}>★</span>;
const Stars5 = ({ rating = 5 }) => (
  <span className="rating-stars">{[1,2,3,4,5].map((i) => <Star key={i} filled={i <= Math.round(rating)} />)}</span>
);

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [categories, setCategories] = useState(DEFAULT_CATS);
  const cat = params.get("category") || "All";

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      if (Array.isArray(data?.categories) && data.categories.length) setCategories(data.categories);
    }).catch(() => {});
  }, []);

  const catList = ["All", ...categories];

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const qs = {};
    if (cat !== "All") qs.category = cat;
    if (q) qs.q = q;
    const { data } = await api.get("/products", { params: qs });
    setProducts(data);
    setLoading(false);
  }, [cat, q]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setCat = (c) => {
    const np = new URLSearchParams(params);
    if (c === "All") np.delete("category");
    else np.set("category", c);
    setParams(np);
  };

  return (
    <div data-testid="shop-page" className="min-h-screen pb-20">
      <section className="pt-14 pb-10 relative overflow-hidden">
        <div className="hero-orb" style={{ width: 520, height: 520, top: -180, right: -160, opacity: .55 }} />
        <div className="max-w-[1180px] mx-auto px-6 relative z-[2]">
          <span className="eyebrow">The full collection</span>
          <h1 className="text-[clamp(2.4rem,5vw,4rem)] mt-3 mb-3">All <em>designs</em></h1>
          <p className="text-[var(--ink-soft)] max-w-[560px]">Filter by vibe. Find your fit. Stick fearlessly.</p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <div className="relative w-full md:w-[320px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--bloom)" }} />
              <input
                data-testid="shop-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search designs…"
                className="w-full rounded-full pl-11 pr-5 py-3 text-sm bg-white border border-[#fde0e8] focus:outline-none focus:border-[var(--bloom)]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {catList.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`category-${c}`}
                  className={`px-5 py-2 rounded-full text-[.78rem] font-bold uppercase tracking-[0.13em] transition ${
                    cat === c ? "text-white shadow-[0_8px_20px_-6px_rgba(224,53,107,.5)]" : "bg-white border border-[#fde0e8] text-[var(--ink)] hover:border-[var(--bloom)]"
                  }`}
                  style={cat === c ? { background: "linear-gradient(135deg, var(--bloom), var(--punch))" } : {}}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1180px] mx-auto px-6 mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="surface aspect-[3/4] animate-pulse" />
              ))
            : products.length === 0
            ? (<div className="col-span-full text-center py-20 text-[var(--ink-soft)]">No designs found. Try a different vibe.</div>)
            : products.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="product-card surface block p-5 relative"
              >
                {p.compare_at && <span className="tag-pill absolute top-4 left-4">Save {Math.round((1 - p.price / p.compare_at) * 100)}%</span>}
                {p.featured && !p.compare_at && <span className="tag-pill new absolute top-4 left-4">New</span>}
                {p.stock === 0 && <span className="tag-pill sold absolute top-4 left-4">Sold out</span>}

                <div className="aspect-square rounded-2xl overflow-hidden mb-4" style={{ background: "linear-gradient(135deg, var(--rose), #fff)" }}>
                  <img src={productImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="eyebrow text-[.65rem] mb-1">{p.category}</div>
                <h3 className="font-serif text-[1.2rem] font-semibold mb-1 leading-tight">{p.name}</h3>
                <div className="text-[.82rem] mb-2">
                  <Stars5 rating={p.avg_rating || 5} />
                  <span className="text-[var(--ink-soft)] ml-1">({p.review_count || 0})</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-[1.2rem] font-semibold">{formatINR(p.price)}</span>
                  {p.compare_at && <span className="text-sm text-[var(--ink-soft)] line-through">{formatINR(p.compare_at)}</span>}
                </div>
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}
